// Learning Coach — sessions the PARENT chose the words for (LB-278).
//
// Alan, 13 Sep: "show the data from custom lists in coach and take the insights (tricky ones)
// but not factor it into banding changes."
//
// Why it matters more than "a custom list is arbitrary": school spelling lists are PRE-TAUGHT.
// A child has practised those exact words for a week, so a high score measures rehearsal, not
// level. Fed to the engine it reads as "too easy, move up" and bands a child above where they
// can work cold. It fails silently and is hard to unpick later.
//
// ── Why this file exists at all ──────────────────────────────────────────────────────────
// The engine (src/lib/adaptive.js) is Fable's and is not edited (Alan, 14 Sep: "I didn't want
// you making code changes to the engine proper"). So this wrapper sits around it:
//
//   1. it enriches the parent's results with the curriculum item each word matches,
//   2. it CALLS the engine normally, so topic mastery is computed by the engine's own code,
//   3. and it then keeps only the fields a custom test is allowed to touch.
//
// Step 3 is a WHITELIST, deliberately, not a list of fields to roll back. A rollback list goes
// stale the moment the engine gains a new banding signal, and it goes stale silently in the
// dangerous direction. A whitelist fails the other way: anything new the engine starts writing
// is simply not carried over, so it cannot leak into promotion without someone choosing to add
// it here.
//
// Calling the engine rather than reimplementing mastery is the other half of that reasoning.
// Two implementations of "what counts as mastered" would drift, and this one decides when a
// band is promoted.
import { adaptiveRecord } from "./adaptive.js";

// The fields a parent-chosen session may write. Everything else the engine returns is dropped.
//   practice — the tricky ones. Not a level signal, and the point of the feature.
//   topics   — mastery. Alan, 14 Sep: an at-band custom word counts towards it, because it is
//              the same curriculum item the engine would have drawn.
//   log      — so Coach can show the test happened, marked as the parent's own list.
const KEEP = ["practice", "topics", "log"];

// Events worth surfacing from a custom session. A target or band move cannot be one: those
// fields are not carried over, so an event announcing them would describe something that did
// not happen.
const KEEP_EVENTS = new Set(["topic_mastered"]);

export function isCustomTrickyWord(entry, word) {
  return entry && entry.word === String(word || "").trim().toLowerCase();
}

// results: the test's own rows, as any game reports them — { id, word, correct, ... }.
// match:   (word) => { band, grade, topic } | null, from the caller. For spelling this is
//          curriculumItemForWord; games without parent-typed words pass nothing.
// Returns { state, events } exactly like adaptiveRecord, so the call site treats both the same.
export function recordCustomSession(stateIn, results, { day, match = null, params } = {}) {
  const before = JSON.parse(JSON.stringify(stateIn || {}));
  const rows = Array.isArray(results) ? results : [];

  // A word is "at band" when the CMS lists it in the child's own working band. That lookup is
  // how a parent-typed word gets a grade and a topic at all: it carries neither of its own,
  // so without the match there is nothing for the engine to credit.
  const enriched = [];
  const tricky = [...(before.customTricky || [])];
  for (const r of rows) {
    const item = match && r.word ? match(r.word) : null;
    const atBand = !!(item && before.band && item.band === before.band);
    if (atBand) {
      enriched.push({
        ...r,
        // The curriculum item's own id, so a miss queues THAT item for practice — the engine's
        // ordinary reinforcement then owns it, including clearing it when the child gets it
        // right cold in an adaptive test.
        id: `auto:${String(r.word).trim().toLowerCase()}`,
        grade: item.grade,
        topic: item.topic,
      });
    } else if (!r.correct && r.word) {
      // Out of band, or in no list at all: an insight for the parent and nothing more. An
      // off-band word put into adaptive practice would be missed repeatedly and could drag the
      // band DOWN, which is the mirror of the problem this whole ticket exists to prevent.
      const w = String(r.word).trim().toLowerCase();
      if (!tricky.some((x) => isCustomTrickyWord(x, w))) tricky.push({ word: w, band: (item && item.band) || null, day });
    }
  }

  const ran = adaptiveRecord(before, enriched, { day, kind: "session" }, params);

  const state = JSON.parse(JSON.stringify(before));
  for (const k of KEEP) if (ran.state[k] !== undefined) state[k] = ran.state[k];
  state.customTricky = tricky.slice(-20);

  // The engine logged only the at-band rows, because those are all it was given. The parent
  // sat a whole test, so the entry is restated with the real score and marked as theirs —
  // Alan, 13 Sep: "it counts as a test but is marked as custom".
  if (Array.isArray(state.log) && state.log.length && (before.log || []).length < state.log.length) {
    const last = state.log[state.log.length - 1];
    state.log = [...state.log.slice(0, -1), { ...last, score: rows.filter((r) => r.correct).length, total: rows.length, source: "manual" }];
  } else {
    state.log = [...(before.log || []), { day, score: rows.filter((r) => r.correct).length, total: rows.length, source: "manual" }].slice(-12);
  }

  return { state, events: (ran.events || []).filter((e) => KEEP_EVENTS.has(e.type)) };
}

// ---- reads for the Coach surfaces --------------------------------------------------------
// A log entry with no `source` predates this and is an engine-drawn test, which is every
// entry written before 14 Sep.
export const isCustomEntry = (e) => !!(e && e.source === "manual");
// A test the child left before the last question (Alan, 22 Sep: all learning activity
// shows, labelled). Logged so the parent sees it happened; excluded from the score trend
// and the monthly average below, and never given to the engine, so it cannot move a level.
export const isStoppedEntry = (e) => !!(e && e.source === "stopped");
export const engineLog = (st) => ((st && st.log) || []).filter((e) => !isCustomEntry(e) && !isStoppedEntry(e));
// Every test that ended with a score: Coach's own and the parent's custom lists alike. The
// score trend on the summary row and the detail page read THIS, not engineLog (Alan, 28 Sep:
// "we seem to have lost the progress graph" — a child doing mostly custom-list tests drew no
// line at all, because those never reached the trend). A stopped test has no score and stays
// out; what the engine is fed is engineLog and is unchanged.
export const scoredLog = (st) => ((st && st.log) || []).filter((e) => !isStoppedEntry(e) && e.total);

// Appends one log entry and touches nothing else — the write path for the two cases the
// engine must not see: a stopped-early test (any household) and every test in a household
// with Learning Coach OFF, which previously recorded nothing at all, so a manual family's
// results reached nobody. Writes only the engine-owned `log` field, from outside the
// engine file, which is the wrapper's licence (Alan, 14 Sep).
export function recordLogOnly(stateIn, { day, score, total, answered, source }) {
  const state = JSON.parse(JSON.stringify(stateIn || {}));
  state.log = [...(state.log || []), { day, score, total, ...(answered !== undefined ? { answered } : {}), source }].slice(-12);
  return state;
}

// Every test in the Monday-start week beginning weekStartStr — stopped and custom entries
// included, because the summary line counts what happened and labels live on the rows.
export function testsInWeek(st, weekStartStr) {
  const ws = Date.parse(weekStartStr);
  if (Number.isNaN(ws)) return 0;
  const we = ws + 7 * 86400000;
  return ((st && st.log) || []).filter((e) => { const d = e.day && Date.parse(e.day); return d >= ws && d < we; }).length;
}

// The Monday one week before weekStartStr — the "last week" column beside "this week".
export function weekBefore(weekStartStr) {
  const t = Date.parse(weekStartStr);
  if (Number.isNaN(t)) return weekStartStr;
  return new Date(t - 7 * 86400000).toISOString().slice(0, 10);
}

// Custom-list tests since fromStr (inclusive) — the "from your list" trailer on the
// summary row, counted over the same window the row shows (this week and last).
export function customTestsSince(st, fromStr) {
  const from = Date.parse(fromStr);
  if (Number.isNaN(from)) return 0;
  return ((st && st.log) || []).filter((e) => isCustomEntry(e) && e.day && Date.parse(e.day) >= from).length;
}

export function customTestsLastWeek(st, todayStr) {
  const since = Date.parse(todayStr) - 7 * 86400000;
  return ((st && st.log) || []).filter((e) => isCustomEntry(e) && e.day && Date.parse(e.day) >= since).length;
}
